import { useEffect, useMemo, useState } from 'react';
import { adminApi } from '../../lib/api.js';
import { useToast } from '../../hooks/useToast.jsx';
import Button from '../../components/ui/Button.jsx';
import Input from '../../components/ui/Input.jsx';
import Select from '../../components/ui/Select.jsx';
import Modal from '../../components/ui/Modal.jsx';
import DataTable from '../../components/ui/DataTable.jsx';
import Badge from '../../components/ui/Badge.jsx';
import { Plus, Minus, RefreshCw, Users, ScanSearch, AlertTriangle, Check } from 'lucide-react';
import { cn, formatDays } from '@/lib/utils';

const currentYear = String(new Date().getFullYear());

function yearOptions() {
  const y = new Date().getFullYear();
  return [y - 1, y, y + 1].map((v) => ({ value: String(v), label: String(v) }));
}

function userLabel(u) {
  return u.displayName || u.fullName || u.email || u.rowKey;
}

function remainingTone(remaining) {
  if (remaining < 0) return 'text-destructive';
  if (remaining === 0) return 'text-warning';
  return 'text-foreground';
}


// Explains itself in the reader's terms: "the counter says X, the requests add
// up to Y". Flags carry the likely cause so nobody has to guess at it.
const FLAG_TEXT = {
  'no-balance-row': 'approved days but no balance row — the write was dropped',
  'no-requests': 'a counter with no requests behind it',
  'float-noise': 'stored with floating-point noise (an unrounded admin edit)',
  'fractional-counter': 'a part-day counter',
  'fractional-request': 'a part-day request behind it',
  'crosses-year-end': 'a request spanning year end — it counts against the year it starts in',
  'matches-previous-used': 'the gap equals the previous-usage figure on their contract',
};

function DriftPanel({ audit, year, onReconcile, reconciling, onDismiss }) {
  const rows = audit.rows || [];
  const drifted = rows.filter((r) => r.usedDrift !== 0 || r.pendingDrift !== 0);

  if (rows.length === 0) {
    return (
      <div className="mb-6 flex items-center gap-3 rounded-lg border border-success/40 bg-success/10 px-4 py-3">
        <Check className="h-4 w-4 text-success shrink-0" />
        <p className="text-sm">
          Every {year} balance matches the requests behind it.
        </p>
        <button onClick={onDismiss} className="ml-auto text-xs text-muted-foreground hover:text-foreground">Dismiss</button>
      </div>
    );
  }

  return (
    <div className="mb-6 rounded-lg border border-warning/40 bg-warning/5">
      <div className="flex items-start gap-3 border-b border-warning/30 px-4 py-3">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
        <div className="min-w-0">
          <p className="text-sm font-semibold">
            {drifted.length} balance{drifted.length === 1 ? '' : 's'} disagree with the requests behind them
            {rows.length > drifted.length && ` · ${rows.length - drifted.length} more worth a look`}
          </p>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Used and pending are running counters. Reconciling sets them to the sum of that person's {year} requests
            and records it on their adjustment history. Only leave types that count against a balance are checked.
          </p>
        </div>
        <button onClick={onDismiss} className="ml-auto shrink-0 text-xs text-muted-foreground hover:text-foreground">Dismiss</button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className="px-4 py-2 font-medium">Person</th>
              <th className="px-4 py-2 font-medium">Type</th>
              <th className="px-4 py-2 font-medium text-right">Counter says</th>
              <th className="px-4 py-2 font-medium text-right">Requests add up to</th>
              <th className="px-4 py-2 font-medium text-right">Difference</th>
              <th className="px-4 py-2 font-medium">Why</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const key = `${r.userId}_${r.typeId}`;
              const off = r.usedDrift !== 0 || r.pendingDrift !== 0;
              return (
                <tr key={key} className="border-t border-border/60 align-top">
                  <td className="px-4 py-2.5 font-medium">{r.userName}</td>
                  <td className="px-4 py-2.5 text-muted-foreground">{r.typeName}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">{formatDays(r.storedUsed)}</td>
                  <td className="px-4 py-2.5 text-right tabular-nums">
                    {formatDays(r.computedUsed)}
                    <span className="ml-1 text-xs text-muted-foreground">({r.requestCount})</span>
                  </td>
                  <td className={cn('px-4 py-2.5 text-right tabular-nums font-semibold', off ? 'text-destructive' : 'text-muted-foreground')}>
                    {r.usedDrift > 0 ? '+' : ''}{formatDays(r.usedDrift)}
                  </td>
                  <td className="px-4 py-2.5">
                    <div className="flex flex-col gap-1">
                      {r.flags.length === 0 && <span className="text-xs text-muted-foreground">no obvious cause</span>}
                      {r.flags.map((f) => (
                        <span key={f} className="text-xs text-muted-foreground">{FLAG_TEXT[f] || f}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    {off && (
                      <Button
                        variant="outline"
                        onClick={() => onReconcile(r)}
                        loading={reconciling === key}
                        disabled={!!reconciling}
                      >
                        Reconcile
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default function BalanceAdjustmentsPage() {
  const toast = useToast();
  const [users, setUsers] = useState([]);
  const [leaveTypes, setLeaveTypes] = useState([]);
  const [balancesByUser, setBalancesByUser] = useState({}); // userId -> { typeId -> balance }
  const [year, setYear] = useState(currentYear);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedCell, setSelectedCell] = useState(null); // { user, type, balance }
  const [showBulkGrant, setShowBulkGrant] = useState(false);
  // Drift check. The used/pending numbers in this grid are running counters
  // maintained by the request lifecycle, not a live sum of the requests — so
  // they can disagree with what someone actually booked. This surfaces that.
  const [audit, setAudit] = useState(null);
  const [auditing, setAuditing] = useState(false);
  const [reconciling, setReconciling] = useState('');

  async function load({ silent = false } = {}) {
    if (silent) setRefreshing(true); else setLoading(true);
    try {
      const meta = await adminApi.listBalances(year);
      const usersList = (meta.users || [])
        .filter((u) => u.isActive !== false)
        .sort((a, b) => userLabel(a).localeCompare(userLabel(b)));
      const types = (meta.leaveTypes || []).filter((t) => t.isActive !== false);

      // Fan out per-user — same call the employee dashboard uses, so what
      // the admin grid shows == what the employee themselves sees.
      const results = await Promise.all(
        usersList.map((u) =>
          adminApi.getUserBalances(u.rowKey, year)
            .then((r) => ({ ok: true, userId: u.rowKey, balances: r.balances || {} }))
            .catch((err) => ({ ok: false, userId: u.rowKey, error: err.message }))
        )
      );

      const map = {};
      const failures = [];
      for (const r of results) {
        if (!r.ok) { failures.push(r); continue; }
        map[r.userId] = r.balances;
      }
      if (failures.length > 0) {
        console.warn('[balances] per-user fetch failures:', failures);
        toast.error(`Couldn't load balances for ${failures.length} user(s) — see console`);
      }

      setUsers(usersList);
      setLeaveTypes(types);
      setBalancesByUser(map);
    } catch (err) {
      toast.error(`Failed to load balances: ${err.message}`);
    } finally {
      if (silent) setRefreshing(false); else setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [year]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reload when the tab regains focus — adjustments and approvals on other
  // pages mutate balances and we want fresh numbers when the admin comes
  // back here.
  useEffect(() => {
    const onFocus = () => load({ silent: true });
    const onVisible = () => { if (document.visibilityState === 'visible') load({ silent: true }); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [year]); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredUsers = useMemo(() => {
    if (!search) return users;
    const s = search.toLowerCase();
    return users.filter(
      (u) =>
        userLabel(u).toLowerCase().includes(s) ||
        (u.email || '').toLowerCase().includes(s)
    );
  }, [users, search]);

  const columns = useMemo(() => {
    const personCol = {
      key: 'person',
      label: 'Person',
      render: (u) => (
        <div className="min-w-[180px]">
          <div className="font-medium">{userLabel(u)}</div>
          {u.email && (
            <div className="text-xs text-muted-foreground truncate">{u.email}</div>
          )}
        </div>
      ),
    };

    const typeCols = leaveTypes.map((t) => ({
      key: `type_${t.rowKey}`,
      label: (
        <div className="flex items-center gap-2">
          <span
            className="inline-block w-2.5 h-2.5 rounded-full"
            style={{ background: t.color || '#64748b' }}
          />
          <span>{t.displayName}</span>
        </div>
      ),
      render: (u) => {
        const b = balancesByUser[u.rowKey]?.[t.rowKey];
        const remaining = b ? Number(b.remaining) : Number(t.defaultAllowance) || 0;
        return (
          <button
            onClick={(e) => {
              e.stopPropagation();
              setSelectedCell({ user: u, type: t, balance: b });
            }}
            className="rounded-md border border-transparent px-2 py-1 text-sm tabular-nums transition-colors hover:border-border hover:bg-accent"
            title="Click to view & adjust"
          >
            <span className={cn('font-semibold', remainingTone(remaining))}>
              {formatDays(remaining)}
            </span>
            {b?.pending > 0 && (
              <span className="ml-1 text-[11px] text-muted-foreground">
                ({formatDays(b.pending)} pending)
              </span>
            )}
          </button>
        );
      },
    }));

    return [personCol, ...typeCols];
  }, [leaveTypes, balancesByUser]);

  async function runAudit() {
    setAuditing(true);
    try {
      const res = await adminApi.auditBalances(year);
      setAudit(res);
      const drifted = res.summary?.driftedRows || 0;
      toast[drifted ? 'error' : 'success'](
        drifted
          ? `${drifted} balance${drifted === 1 ? '' : 's'} disagree with the requests`
          : 'Every balance matches its requests',
      );
    } catch (err) {
      toast.error(err.message || 'Could not run the check');
    } finally {
      setAuditing(false);
    }
  }

  async function reconcile(row) {
    const key = `${row.userId}_${row.typeId}`;
    setReconciling(key);
    try {
      await adminApi.reconcileBalance({ userId: row.userId, typeId: row.typeId, year });
      toast.success(`${row.userName}: ${row.typeName} used set to ${formatDays(row.computedUsed)}`);
      await runAudit();
      await load({ silent: true });
    } catch (err) {
      toast.error(err.message || 'Could not reconcile');
    } finally {
      setReconciling('');
    }
  }

  return (
    <div>
      <div className="flex justify-between items-start mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight mb-2">Balance Adjustments</h1>
          <p className="text-muted-foreground">
            Click any number to view that person's balance and apply an
            adjustment. Each change is recorded in the audit log.
          </p>
        </div>
        <div className="flex gap-3 items-end">
          <Select
            label="Year"
            value={year}
            onChange={(e) => setYear(e.target.value)}
            options={yearOptions()}
          />
          <Button
            variant="outline"
            onClick={() => load({ silent: true })}
            disabled={loading || refreshing}
          >
            <RefreshCw className={cn('h-4 w-4', (loading || refreshing) && 'animate-spin')} />
            Refresh
          </Button>
          <Button
            variant="outline"
            onClick={runAudit}
            disabled={auditing}
          >
            <ScanSearch className={cn('h-4 w-4', auditing && 'animate-pulse')} />
            Check against requests
          </Button>
          <Button onClick={() => setShowBulkGrant(true)}>
            <Users className="h-4 w-4" />
            Bulk Grant
          </Button>
        </div>
      </div>

      <div className="mb-4">
        <Input
          placeholder="Search people..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="max-w-[400px]"
        />
      </div>

      <DataTable
        columns={columns}
        rows={filteredUsers}
        loading={loading}
        emptyMessage={
          users.length === 0
            ? 'No active users yet.'
            : 'No people match your search.'
        }
        keyFn={(u) => u.rowKey}
      />

      {showBulkGrant && (
        <BulkGrantModal
          leaveTypes={leaveTypes}
          users={users}
          year={year}
          onClose={() => setShowBulkGrant(false)}
          onGranted={async () => {
            setShowBulkGrant(false);
            await load({ silent: true });
          }}
        />
      )}

      {selectedCell && (
        <BalanceCellModal
          user={selectedCell.user}
          type={selectedCell.type}
          // Pull live from state so post-adjustment reload is reflected
          // in the modal — stashing a frozen snapshot here causes a
          // stale-closure bug on the refresh callback.
          balance={balancesByUser[selectedCell.user.rowKey]?.[selectedCell.type.rowKey]}
          year={year}
          onClose={() => setSelectedCell(null)}
          onAdjusted={() => load({ silent: true })}
        />
      )}
    </div>
  );
}

function BalanceCellModal({ user, type, balance, year, onClose, onAdjusted }) {
  const toast = useToast();
  const [delta, setDelta] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const allowance = Number(balance?.allowance) || 0;
  const used = Number(balance?.used) || 0;
  const pending = Number(balance?.pending) || 0;
  const carriedOver = Number(balance?.carriedOver) || 0;
  const remaining = balance
    ? Number(balance.remaining)
    : (Number(type.defaultAllowance) || 0);

  async function applyDelta(sign) {
    const raw = Number(delta);
    if (!Number.isFinite(raw) || raw === 0) {
      toast.error('Enter a non-zero number of days');
      return;
    }
    if (!reason || reason.trim().length < 3) {
      toast.error('Reason is required (3+ characters) — it lands in the audit log');
      return;
    }
    const signed = sign * Math.abs(raw);
    setSaving(true);
    try {
      await adminApi.adjustBalance({
        userId: user.rowKey,
        typeId: type.rowKey,
        year: Number(year),
        delta: signed,
        reason: reason.trim(),
      });
      toast.success(
        `${signed > 0 ? 'Added' : 'Removed'} ${formatDays(Math.abs(signed))} day(s) of ${type.displayName}`
      );
      setDelta('');
      setReason('');
      await onAdjusted();
    } catch (err) {
      toast.error(`Adjustment failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={`${userLabel(user)} — ${type.displayName}`}
      width="540px"
      footer={
        <Button variant="ghost" onClick={onClose}>Close</Button>
      }
    >
      <div className="space-y-5">
        <div className="flex items-center justify-between rounded-lg border border-border bg-background/40 px-4 py-3">
          <div>
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              Remaining ({year})
            </div>
            <div className={cn('text-2xl font-semibold tabular-nums', remainingTone(remaining))}>
              {formatDays(remaining)}
            </div>
          </div>
          <Badge variant={remaining < 0 ? 'danger' : remaining === 0 ? 'warning' : 'success'}>
            {remaining < 0 ? 'Over' : remaining === 0 ? 'Empty' : 'OK'}
          </Badge>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <Stat label="Allowance" value={formatDays(allowance)} />
          <Stat label="Carried over" value={formatDays(carriedOver)} />
          <Stat label="Used" value={formatDays(used)} />
          <Stat label="Pending" value={formatDays(pending)} />
        </div>

        <div className="rounded-lg border border-border bg-background/40 p-4">
          <div className="text-xs font-medium text-muted-foreground mb-3">
            Apply adjustment to allowance
          </div>
          <div className="space-y-3">
            <div className="grid grid-cols-[120px_1fr] gap-3">
              <Input
                type="number"
                step="0.25"
                min="0"
                placeholder="Days"
                value={delta}
                onChange={(e) => setDelta(e.target.value)}
              />
              <Input
                placeholder="Reason (audit trail)"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button
                variant="outline"
                onClick={() => applyDelta(-1)}
                disabled={saving}
              >
                <Minus className="h-4 w-4" />
                Remove
              </Button>
              <Button
                onClick={() => applyDelta(+1)}
                disabled={saving}
                loading={saving}
              >
                <Plus className="h-4 w-4" />
                Add
              </Button>
            </div>
          </div>
        </div>

        <details className="text-xs text-muted-foreground">
          <summary className="cursor-pointer select-none">Diagnostics</summary>
          <div className="mt-2 space-y-1 font-mono">
            <div>userId (partition): <span className="text-foreground">{user.rowKey}</span></div>
            <div>email: <span className="text-foreground">{user.email || '—'}</span></div>
            <div>typeId: <span className="text-foreground">{type.rowKey}</span></div>
            <div>year: <span className="text-foreground">{year}</span></div>
          </div>
        </details>
      </div>
    </Modal>
  );
}

function BulkGrantModal({ leaveTypes, users, year, onClose, onGranted }) {
  const toast = useToast();
  const [typeId, setTypeId] = useState(leaveTypes[0]?.rowKey || '');
  const [scope, setScope] = useState('all'); // 'all' | 'users'
  const [pickedUserIds, setPickedUserIds] = useState(new Set());
  const [delta, setDelta] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  function togglePick(id) {
    setPickedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  async function handleGrant() {
    if (!typeId) { toast.error('Pick a leave type'); return; }
    const raw = Number(delta);
    if (!Number.isFinite(raw) || raw === 0) {
      toast.error('Enter a non-zero number of days to grant (negative to remove)');
      return;
    }
    if (!reason || reason.trim().length < 3) {
      toast.error('Reason is required (3+ characters)');
      return;
    }
    if (scope === 'users' && pickedUserIds.size === 0) {
      toast.error('Pick at least one user, or change scope to "Everyone"');
      return;
    }

    const target = scope === 'all'
      ? `${users.length} active user(s)`
      : `${pickedUserIds.size} selected user(s)`;
    if (!window.confirm(`Grant ${raw} day(s) of ${typeId} for ${year} to ${target}?`)) return;

    setSaving(true);
    try {
      const body = {
        typeId,
        year: Number(year),
        delta: Math.round(raw * 100) / 100,
        reason: reason.trim(),
        scope,
      };
      if (scope === 'users') body.userIds = Array.from(pickedUserIds);

      const res = await adminApi.bulkGrant(body);
      toast.success(`Updated ${res.updated} balance row(s)`);
      await onGranted();
    } catch (err) {
      toast.error(`Bulk grant failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title={`Bulk grant — ${year}`}
      width="640px"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleGrant} loading={saving}>Apply grant</Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="rounded-md border border-border bg-background/40 p-3 text-xs text-muted-foreground">
          Adds <span className="font-mono text-foreground">delta</span> to each
          recipient's allowance for the chosen leave type. Existing
          allowance, used, pending, and carryover are preserved. Use a
          negative delta to remove days. Every change is recorded in the
          audit log.
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Select
            label="Leave type"
            value={typeId}
            onChange={(e) => setTypeId(e.target.value)}
            options={leaveTypes.map((t) => ({ value: t.rowKey, label: t.displayName }))}
          />
          <Input
            label="Days to grant"
            type="number"
            step="0.25"
            value={delta}
            onChange={(e) => setDelta(e.target.value)}
            placeholder="e.g. 21 (or -5 to remove)"
          />
        </div>

        <Input
          label="Reason (audit trail)"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. 2026 annual entitlement"
        />

        <div>
          <div className="text-[13px] font-medium text-muted-foreground mb-1.5">
            Apply to
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setScope('all')}
              className={cn(
                'flex-1 rounded-md border px-3 py-2 text-sm transition-colors',
                scope === 'all'
                  ? 'border-primary bg-primary/10 text-foreground font-medium'
                  : 'border-border bg-card text-muted-foreground hover:bg-accent'
              )}
            >
              Everyone ({users.length})
            </button>
            <button
              type="button"
              onClick={() => setScope('users')}
              className={cn(
                'flex-1 rounded-md border px-3 py-2 text-sm transition-colors',
                scope === 'users'
                  ? 'border-primary bg-primary/10 text-foreground font-medium'
                  : 'border-border bg-card text-muted-foreground hover:bg-accent'
              )}
            >
              Pick users ({pickedUserIds.size})
            </button>
          </div>
        </div>

        {scope === 'users' && (
          <div className="rounded-md border border-border bg-background/40 max-h-[260px] overflow-y-auto p-2">
            {users.length === 0 ? (
              <div className="text-sm text-muted-foreground p-2">No users to pick from.</div>
            ) : (
              users.map((u) => {
                const checked = pickedUserIds.has(u.rowKey);
                return (
                  <label
                    key={u.rowKey}
                    className="flex items-center gap-2 rounded px-2 py-1.5 cursor-pointer hover:bg-accent"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => togglePick(u.rowKey)}
                    />
                    <span className="text-sm">{u.displayName || u.email || u.rowKey}</span>
                    {u.email && (
                      <span className="text-xs text-muted-foreground ml-auto">{u.email}</span>
                    )}
                  </label>
                );
              })
            )}
          </div>
        )}
      </div>
    </Modal>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-md bg-background/40 border border-border px-3 py-2">
      <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
        {label}
      </div>
      <div className="font-semibold tabular-nums">{value}</div>
    </div>
  );
}
